import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import {
  createRotable,
  deleteRotable,
  fitRotableToAsset,
  getPaginatedRotables,
  getRotableById,
  quarantineRotable,
  releaseFromQuarantine,
  awaitingPickupRotable,
  receiveRotableFromSupplier,
  removeRotableFromAsset,
  adjustRotable,
  scrapRotable,
  sellRotable,
  sendRotableToSupplier,
  updateRotable,
} from '../api/rotablesAPI';
import RotablesDataTable from './RotablesDataTable';
import RotableModal from './RotableModal';
import RotableTransactionModal from './RotableTransactionModal';
import RotableViewModal from './RotableViewModal';
import { formatIntDate } from '../utils/dateUtils';
import { loadSupplierOptions } from '../utils/supplierOptionUtils';
import { useTableFilters } from '../utils/useTableFilters';

const INITIAL_PAGE_SIZE = 25;
const LOAD_MORE_SIZE = 5;
const NEXT_PAGE_AFTER_INITIAL = Math.floor(INITIAL_PAGE_SIZE / LOAD_MORE_SIZE) + 1;

const RotablesLayer = () => {
  const [rotables, setRotables] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pageSize: INITIAL_PAGE_SIZE });
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRotable, setEditingRotable] = useState(null);
  const listContainerRef = useRef(null);
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [transactionModalOpen, setTransactionModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedRotable, setSelectedRotable] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRotable, setViewRotable] = useState(null);

  const transactionActions = useMemo(
    () => [
      { key: 'receive', label: 'Receive', icon: 'mdi:package-down' },
      { key: 'fit', label: 'Fit to Asset', icon: 'mdi:cog-play' },
      { key: 'remove', label: 'Remove from Asset', icon: 'mdi:cog-off' },
      { key: 'quarantine', label: 'In Quarantine', icon: 'mdi:biohazard' },
      { key: 'quarantine-release', label: 'Release From Quarantine', icon: 'mdi:shield-check' },
      { key: 'awaiting-pickup', label: 'Holding', icon: 'mdi:package-variant-closed' },
      { key: 'send-to-supplier', label: 'Send To Supplier', icon: 'mdi:truck-fast' },
      { key: 'scrap', label: 'Scrap', icon: 'mdi:delete' },
      { key: 'sell', label: 'Sell', icon: 'mdi:cash' },
      { type: 'divider', key: 'divider-adjustment' },
      { key: 'adjustment', label: 'Manual Adjustment', icon: 'mdi:swap-vertical' },
    ],
    [],
  );

  const tableFilters = useMemo(
    () => [
      {
        field: 'stockItemsStatusId',
        type: 'dropDown',
        label: 'Status',
        api: 'stock-item-status',
        displayField: 'name',
        isMulti: false,
        menuBarFilter: 1,
      },
      {
        field: 'repairStatus',
        type: 'dropDown',
        label: 'Repair Status',
        api: 'repair-status',
        displayField: 'name',
        isMulti: false,
        menuBarFilter: 1,
      },
      {
        field: 'supplierId',
        type: 'paginatedDropdown',
        label: 'Supplier',
        loadOptions: loadSupplierOptions,
        isMulti: false,
      },
      {
        field: 'assetId',
        type: 'dropDown',
        label: 'Asset',
        api: 'assets/for-dropdown',
        displayField: (asset) => `${asset.unitNumber || asset.assetNumber || ''} ${asset.assetDescription || ''}`,
        isMulti: false,
      },
    ],
    [],
  );

  const {
    appliedFilterParams,
    renderFilterControls,
  } = useTableFilters(tableFilters);

  const resetPagination = useCallback(() => {
    setPagination({ page: 1, pageSize: INITIAL_PAGE_SIZE });
    setRotables([]);
    setHasMore(true);
  }, []);

  useEffect(() => {
    resetPagination();
  }, [appliedFilterParams, resetPagination, searchTerm, sortField, sortOrder]);

  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const resp = await getPaginatedRotables({
          page: pagination.page,
          pageSize: pagination.pageSize,
          search: searchTerm,
          sortField: sortField || undefined,
          sortOrder: sortOrder || undefined,
          ...appliedFilterParams,
        });

        setRotables((prev) => {
          const base = pagination.page === 1 ? [] : prev;
          const incoming = resp.data || [];
          const merged = [...base, ...incoming];
          setHasMore(merged.length < resp.total);
          return merged;
        });
      } catch (err) {
        console.error('Error loading rotables', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [pagination, searchTerm]);

  const handleLoadMore = useCallback(() => {
    if (loading || !hasMore) return;

    setPagination((prev) => {
      if (prev.page === 1 && prev.pageSize === INITIAL_PAGE_SIZE) {
        return { page: NEXT_PAGE_AFTER_INITIAL, pageSize: LOAD_MORE_SIZE };
      }

      return { page: prev.page + 1, pageSize: LOAD_MORE_SIZE };
    });
  }, [loading, hasMore]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return undefined;

    const onScroll = () => {
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - 5) {
        handleLoadMore();
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [handleLoadMore]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = useCallback(
    (field) => {
      if (listContainerRef.current) {
        listContainerRef.current.scrollTop = 0;
      }
      resetPagination();
      if (sortField === field) {
        if (sortOrder === 'asc') {
          setSortOrder('desc');
        } else if (sortOrder === 'desc') {
          setSortField(null);
          setSortOrder(null);
        } else {
          setSortOrder('asc');
        }
      } else {
        setSortField(field);
        setSortOrder('asc');
      }
    },
    [resetPagination, sortField, sortOrder],
  );

  const handleSaveRotable = async (payload) => {
    try {
      if (editingRotable?.id) {
        await updateRotable(editingRotable.id, payload);
      } else {
        await createRotable(payload);
      }
      resetPagination();
      setShowModal(false);
      setEditingRotable(null);
    } catch (err) {
      console.error('Error saving rotable', err);
    }
  };

  const handleEdit = async (rotable) => {
    try {
      const data = await getRotableById(rotable.id);
      setEditingRotable(data);
      setShowModal(true);
    } catch (err) {
      console.error('Error fetching rotable', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this rotable?')) return;
    try {
      await deleteRotable(id);
      resetPagination();
    } catch (err) {
      console.error('Error deleting rotable', err);
    }
  };

  const handleOpenTransactionModal = (rotable, action) => {
    setSelectedRotable(rotable);
    setSelectedTransaction(action);
    setTransactionModalOpen(true);
  };

  const handleView = (rotable) => {
    setViewRotable(rotable);
    setViewModalOpen(true);
  };

  const normalizeRotableForList = useCallback((rotable) => {
    if (!rotable) return rotable;
    const task = rotable.task ?? (Array.isArray(rotable.tasks) ? rotable.tasks[0] : null);
    return {
      ...rotable,
      task,
      taskId: rotable.taskId ?? task?.id ?? null,
    };
  }, []);

  const buildTransactionPayload = (actionKey, rotable, details) => {
    const payload = {
      notes: details?.notes || '',
      files: details?.files || [],
      images: details?.images || [],
    };
    const locationActions = new Set(['receive', 'remove', 'quarantine', 'awaiting-pickup', 'adjustment']);
    payload.locationId = locationActions.has(actionKey) ? (details?.locationId ?? null) : null;

    if (actionKey === 'send-to-supplier') {
      const supplierId = rotable?.supplier?.id ?? rotable?.supplierId ?? null;
      if (supplierId) {
        payload.supplierId = supplierId;
      }
    }

    if (actionKey === 'fit') {
      payload.assetId = details?.assetId ?? null;
      payload.taskId = details?.taskId ?? null;
    }

    if (actionKey === 'remove') {
      const assetId = rotable?.task?.assetId ?? null;
      if (assetId) {
        payload.assetId = assetId;
      }
    }

    if (actionKey === 'awaiting-pickup') {
      payload.repairStatusId = details?.repairStatusId ?? null;
    }

    if (actionKey === 'adjustment') {
      payload.stockItemStatusId = details?.stockItemStatusId ?? null;
      payload.repairStatusId = details?.repairStatusId ?? null;
    }

    if (actionKey === 'quarantine-release') {
      payload.stockItemStatusId = details?.stockItemStatusId ?? null;
      payload.repairStatusId = details?.repairStatusId ?? null;
    }

    return payload;
  };

  const handleTransactionSave = async (details) => {
    if (!selectedRotable?.id || !selectedTransaction?.key) return;
    const actionKey = selectedTransaction.key;
    const payload = buildTransactionPayload(actionKey, selectedRotable, details);

    try {
      let updatedRotable = null;
      switch (actionKey) {
        case 'receive':
          updatedRotable = await receiveRotableFromSupplier(selectedRotable.id, payload);
          break;
        case 'fit':
          updatedRotable = await fitRotableToAsset(selectedRotable.id, payload);
          break;
        case 'remove':
          updatedRotable = await removeRotableFromAsset(selectedRotable.id, payload);
          break;
        case 'quarantine':
          updatedRotable = await quarantineRotable(selectedRotable.id, payload);
          break;
        case 'quarantine-release':
          updatedRotable = await releaseFromQuarantine(selectedRotable.id, payload);
          break;
        case 'awaiting-pickup':
          updatedRotable = await awaitingPickupRotable(selectedRotable.id, payload);
          break;
        case 'send-to-supplier':
          updatedRotable = await sendRotableToSupplier(selectedRotable.id, payload);
          break;
        case 'scrap':
          updatedRotable = await scrapRotable(selectedRotable.id, payload);
          break;
        case 'sell':
          updatedRotable = await sellRotable(selectedRotable.id, payload);
          break;
        case 'adjustment':
          updatedRotable = await adjustRotable(selectedRotable.id, payload);
          break;
        default:
          break;
      }

      if (updatedRotable?.id) {
        const normalizedRotable = normalizeRotableForList(updatedRotable);
        setRotables((prev) =>
          prev.map((item) =>
            item.id === updatedRotable.id
              ? {
                  ...item,
                  ...normalizedRotable,
                  task: normalizedRotable.task ?? null,
                }
              : item,
          ),
        );
      }

      setTransactionModalOpen(false);
      setSelectedRotable(null);
      setSelectedTransaction(null);
    } catch (err) {
      console.error('Error applying rotable transaction', err);
    }
  };

  const getLocationLabel = (rotable) =>
    [rotable?.zone, rotable?.aisle, rotable?.bay, rotable?.shelf]
      .filter(Boolean)
      .join(' / ');

  const getWarrantyLabel = (rotable) => {
    if (!rotable?.warrantyStart && !rotable?.warrantyEnd) return '';
    return `${formatIntDate(rotable?.warrantyStart)} - ${formatIntDate(rotable?.warrantyEnd)}`;
  };

  const getAssetLabel = (rotable) =>
    rotable?.asset?.assetNumber ||
    rotable?.assetNumber ||
    rotable?.task?.asset?.assetNumber ||
    '';

  const allowedTransactionsByStatus = useMemo(
    () => ({
      'In Stock': new Set(['fit', 'scrap', 'sell', 'adjustment']),
      Fitted: new Set(['remove', 'quarantine', 'scrap', 'sell', 'adjustment']),
      'In Quarantine': new Set(['awaiting-pickup', 'scrap', 'quarantine-release', 'sell', 'adjustment']),
      'Release From Quarantine': new Set(['awaiting-pickup', 'scrap', 'sell', 'fit', 'adjustment']),
      'Awaiting Pickup': new Set(['send-to-supplier', 'scrap', 'sell', 'adjustment']),
      'At Supplier': new Set(['receive', 'scrap', 'sell', 'adjustment']),
      Sold: new Set(['adjustment']),
      Scrapped: new Set(['adjustment']),
    }),
    [],
  );

  const isTransactionDisabled = useCallback(
    (rotable, action) => {
      if (!action?.key) return false;
      const statusName = rotable?.status?.name ?? '';
      const allowed = allowedTransactionsByStatus[statusName] ?? new Set();
      return !allowed.has(action.key);
    },
    [allowedTransactionsByStatus],
  );

  const generateCsv = () => {
    const headers = [
      'Serial Number',
      'Part Number',
      'Description',
      'Status',
      'Repair Status',
      'Supplier',
      'Location',
      'Asset',
      'Warranty',
    ];

    const rows = rotables.map((rotable) => [
      rotable?.serialNumber || '',
      rotable?.part?.partNumber ?? rotable?.partNumber ?? '',
      rotable?.part?.description ?? '',
      rotable?.status?.name ?? '',
      rotable?.repairStatus?.name ?? '',
      rotable?.supplier?.name ?? '',
      getLocationLabel(rotable),
      getAssetLabel(rotable),
      getWarrantyLabel(rotable),
    ]);

    const csvLines = [
      headers.join(','),
      ...rows.map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')),
    ];
    return csvLines.join('\n');
  };

  const handleExportExcel = () => {
    const headers = [
      'Serial Number',
      'Part Number',
      'Description',
      'Status',
      'Repair Status',
      'Supplier',
      'Location',
      'Asset',
      'Warranty',
    ];

    const rows = rotables.map((rotable) => [
      rotable?.serialNumber || '',
      rotable?.part?.partNumber ?? rotable?.partNumber ?? '',
      rotable?.part?.description ?? '',
      rotable?.status?.name ?? '',
      rotable?.repairStatus?.name ?? '',
      rotable?.supplier?.name ?? '',
      getLocationLabel(rotable),
      getAssetLabel(rotable),
      getWarrantyLabel(rotable),
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Rotables');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'rotables.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csv = generateCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'rotables.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card">
      {headerSearchContainer &&
        createPortal(
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: '200px' }}
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchChange}
          />,
          headerSearchContainer,
        )}
      <div className="card-header bg-white d-flex justify-content-between align-items-center">
        <div className="d-flex flex-wrap align-items-center gap-2 ms-3">
          {renderFilterControls()}
        </div>
        <div className="d-flex align-items-center gap-2 ms-auto me-3">
          <div className="dropdown">
            <button
              className="btn btn-outline-secondary text-sm px-12 py-12 radius-4 dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              Export Data
            </button>
            <ul className="dropdown-menu p-12 border bg-base shadow">
              <li>
                <button className="dropdown-item" onClick={handleExportExcel}>
                  Excel
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={handleExportCSV}>
                  CSV
                </button>
              </li>
            </ul>
          </div>
          <button className="btn btn-primary d-inline-flex align-items-center" onClick={() => setShowModal(true)}>
            <Icon icon="mage:plus" className="me-2" /> Add Rotable
          </button>
        </div>
      </div>
      <div className="card-body p-0">
        <div className="paginated-table-container scroll-sm" ref={listContainerRef} style={{ height: 'calc(100vh - 206px)' }}>
          <RotablesDataTable
            rotables={rotables}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onTransaction={handleOpenTransactionModal}
            transactionActions={transactionActions}
            isTransactionDisabled={isTransactionDisabled}
            loading={loading}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSortChange}
          />
        </div>
      </div>

      {showModal && (
        <RotableModal
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingRotable(null);
          }}
          onSave={handleSaveRotable}
          initialValues={editingRotable}
        />
      )}

      {transactionModalOpen && (
        <RotableTransactionModal
          show={transactionModalOpen}
          action={selectedTransaction}
          rotable={selectedRotable}
          onClose={() => {
            setTransactionModalOpen(false);
            setSelectedRotable(null);
            setSelectedTransaction(null);
          }}
          onSave={handleTransactionSave}
        />
      )}

      {viewModalOpen && (
        <RotableViewModal
          show={viewModalOpen}
          rotableId={viewRotable?.id}
          initialRotable={viewRotable}
          onClose={() => {
            setViewModalOpen(false);
            setViewRotable(null);
          }}
        />
      )}
    </div>
  );
};

export default RotablesLayer;
